import { useMemo } from 'react';
import { useExperiment } from '@acp/feature/auth';
import { Experiment } from '@acp/shared';
import { useOverdueInvoices } from '../components/overdue-invoices-tab/use-overdue-invoices';
import { useActiveIntegrations } from '../../integration';
import { useUrgentActiveNotices } from '../components/urgent-active-notices-tab/use-urgent-active-notices';
import { useNewManagements } from '../../integrated-portfolio';
import { useUrgentAccessIssues } from '../../access-issues';
import { useUrgentReportedFindings } from '../../reported-findings/hooks/use-reported-findings';
import { usePropertyManager } from '../../agency';

interface QueryType {
    isLoading: boolean;
    data?: {
        total: number;
    };
}

function useTabText(query: QueryType) {
    if (!query.isLoading && query.data !== undefined && query.data.total !== 0) {
        return ` (${query.data.total})`;
    }

    return '';
}

export function useActionRequiredCard(propertyManagerId?: string) {
    const { hasExperiment } = useExperiment();
    const isAccessIssuesEnabled = useMemo(
        () => hasExperiment(Experiment.AgencyEntryMethod),
        [hasExperiment]
    );

    const overdueInvoiceQuery = useOverdueInvoices({ propertyManagerId });
    const newManagementsQuery = useNewManagements();
    const urgentActiveNoticesQuery = useUrgentActiveNotices({ propertyManagerId });
    const urgentAccessIssuesQuery = useUrgentAccessIssues(propertyManagerId);
    const urgentReportedFindingsQuery = useUrgentReportedFindings(propertyManagerId);

    const { isIntegrated } = useActiveIntegrations();
    const { propertyManager } = usePropertyManager(propertyManagerId ?? null);

    const numberOfUrgentReportedFindings = useTabText(urgentReportedFindingsQuery);
    const numberOfNewManagements = useTabText(newManagementsQuery);
    const numberOfUrgentAccessIssues = useTabText(urgentAccessIssuesQuery);
    const numberOfActiveNotices = useTabText(urgentActiveNoticesQuery);
    const numberOfOverdueInvoices = useTabText(overdueInvoiceQuery);

    const urgentReportedFindingsTabText = `Resolve reported findings ${numberOfUrgentReportedFindings}`;
    const newManagementTabText = `Enrol new managements ${numberOfNewManagements}`;
    const accessIssuesTabText = isAccessIssuesEnabled
        ? `Resolve access issues ${numberOfUrgentAccessIssues}`
        : `Resolve access issues ${numberOfActiveNotices}`;
    const overdueInvoicesTabText = `Pay overdue invoices ${numberOfOverdueInvoices}`;

    const tabDropdownItems = [
        newManagementTabText,
        urgentReportedFindingsTabText,
        accessIssuesTabText,
        overdueInvoicesTabText,
    ]
        .filter(Boolean)
        .filter((item) => (isIntegrated ? true : !item.includes('Enrol new managements')));

    return {
        isIntegrated,
        isAccessIssuesEnabled,
        propertyManager,
        urgentReportedFindingsTabText,
        newManagementTabText,
        accessIssuesTabText,
        overdueInvoicesTabText,
        tabDropdownItems,
    };
}
